// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@burdenoff/fe-libs/ui';
import { GitBranch, Calendar, Package, Loader2 } from 'lucide-react';
import { usePlanMagnet } from '../providers/PlanMagnetProvider';

const LIST_RELEASES = gql`
  query ListReleases($workspaceId: ID!, $filter: ReleaseFilter, $pagination: PaginationInput) {
    planmagnetReleases(workspaceId: $workspaceId, filter: $filter, pagination: $pagination) {
      items {
        id
        workspaceId
        projectId
        name
        description
        version
        status
        plannedDate
        releasedAt
        releaseNotes
        ownerId
        tags
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

interface ReleaseNode {
  id: string;
  name: string;
  description: string | null;
  version: string;
  status: string;
  plannedDate: string | null;
  releasedAt: string | null;
  releaseNotes: string | null;
  ownerId: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

const statusTabs = [
  'all',
  'DRAFT',
  'PLANNING',
  'IN_PROGRESS',
  'READY',
  'RELEASED',
  'CANCELLED',
] as const;
const statusColor: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  PLANNING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  READY: 'bg-orange-100 text-orange-800',
  RELEASED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  ROLLED_BACK: 'bg-red-100 text-red-800',
};

export function ProductsReleasesPagePage() {
  const { workspaceId } = usePlanMagnet();
  const [activeTab, setActiveTab] = useState<string>('all');

  const {
    data,
    loading,
    error,
  } = // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useQuery<Record<string, any>>(LIST_RELEASES, {
      variables: {
        workspaceId,
        filter: activeTab !== 'all' ? { status: activeTab } : undefined,
        pagination: { first: 50 },
      },
      skip: !workspaceId,
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-destructive">Failed to load releases: {error.message}</div>;
  }

  const releases: ReleaseNode[] = data?.planmagnetReleases?.items ?? [];
  const totalCount = data?.planmagnetReleases?.totalCount ?? 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Releases</h1>
          <p className="text-sm text-muted-foreground">{totalCount} releases across all products</p>
        </div>
        <Button size="sm">
          <GitBranch className="mr-1 h-4 w-4" />
          New Release
        </Button>
      </div>

      <div className="flex gap-1 border-b pb-1">
        {statusTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-sm rounded-md capitalize ${activeTab === tab ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
          >
            {tab === 'all' ? 'All' : tab.toLowerCase().replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {releases.map((release) => (
          <Card key={release.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">{release.version}</span>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColor[release.status] ?? 'bg-gray-100'}`}
                >
                  {release.status.toLowerCase().replace('_', ' ')}
                </span>
              </div>
              <CardTitle className="text-sm font-medium">{release.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {release.description ?? 'No description'}
              </p>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Package className="h-3.5 w-3.5" />
                  Owner: {release.ownerId}
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  {release.plannedDate
                    ? new Date(release.plannedDate).toLocaleDateString()
                    : 'No date'}
                </div>
              </div>

              {release.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {release.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {release.releaseNotes && (
                <div className="border-t pt-2">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Release Notes Preview
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-3">
                    {release.releaseNotes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {releases.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No releases match this filter.</p>
      )}
    </div>
  );
}
